import React, { useState, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Users, Settings2, CheckCircle, XCircle, RefreshCw, Scale, ExternalLink, BarChart3 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { StatusBadge, LedgerTable } from '@/components/ManagerCashPanel';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const SYNC_STYLES = {
  synced: 'text-emerald-400', sync_failed: 'text-red-400', not_eligible: 'text-zinc-400',
  not_synced: 'text-wt-text-muted', sync_pending: 'text-amber-400',
};

const AdminCashPanel = forwardRef(({ formatCurrency, onSettled }, ref) => {
  const navigate = useNavigate();
  const [wallets, setWallets] = useState([]);
  const [pending, setPending] = useState({ ledger_items: [], pending_expenses: [] });
  const [settings, setSettings] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [settleFor, setSettleFor] = useState(null);
  const [summary, setSummary] = useState(null);
  const [actualSubmitted, setActualSubmitted] = useState('');
  const [settleNotes, setSettleNotes] = useState('');
  const [viewLedgerFor, setViewLedgerFor] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [rejectFor, setRejectFor] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  const load = useCallback(async () => {
    try {
      const [w, p, s] = await Promise.all([
        axios.get(`${API}/manager-cash/wallets`, auth()),
        axios.get(`${API}/manager-cash/pending-approvals`, auth()),
        axios.get(`${API}/manager-cash/settings`, auth()),
      ]);
      setWallets(w.data.wallets || []);
      setPending(p.data);
      setSettings(s.data);
    } catch (e) { /* not admin */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  const approve = async (id) => {
    try {
      const resp = await axios.post(`${API}/manager-cash/entries/${id}/approve`, {}, auth());
      const sync = resp.data?.shopify_sync?.status;
      toast.success(`Approved${sync === 'synced' ? ' — Shopify order marked paid' : sync === 'sync_failed' ? ' — Shopify sync failed (retry available)' : ''}`);
      window.dispatchEvent(new Event('pos-shifts-refresh'));
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Approve failed'); }
  };

  const reject = async () => {
    try {
      await axios.post(`${API}/manager-cash/entries/${rejectFor.id}/reject`, { reason: rejectReason }, auth());
      toast.success('Rejected');
      setRejectFor(null); setRejectReason('');
      window.dispatchEvent(new Event('pos-shifts-refresh'));
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Reject failed'); }
  };

  const approveExpense = async (id) => {
    try {
      await axios.post(`${API}/expenses/${id}/approve`, {}, auth());
      toast.success('Expense approved — deducted from manager cash');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Approve failed'); }
  };

  const rejectExpense = async (id) => {
    const reason = window.prompt('Rejection reason:');
    if (!reason) return;
    try {
      await axios.post(`${API}/expenses/${id}/reject`, { reason }, auth());
      toast.success('Expense rejected — no effect on balance');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Reject failed'); }
  };

  const openSettle = async (managerId) => {
    try {
      const resp = await axios.get(`${API}/manager-cash/settlement-summary?manager_id=${managerId}`, auth());
      setSummary(resp.data);
      setActualSubmitted(String(resp.data.expected_cash));
      setSettleFor(managerId);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load summary'); }
  };

  useImperativeHandle(ref, () => ({ openSettle }));

  const settle = async () => {
    try {
      const resp = await axios.post(`${API}/manager-cash/settle`, {
        manager_id: settleFor,
        actual_submitted: parseFloat(actualSubmitted) || 0,
        notes: settleNotes,
      }, auth());
      const d = resp.data.difference;
      toast.success(`Settlement approved${Math.abs(d) > 0.01 ? ` — ${d < 0 ? 'shortage' : 'excess'} ${formatCurrency(Math.abs(d))} recorded` : ''}`);
      const syncs = resp.data.shopify_sync_results || [];
      const synced = syncs.filter((s) => s.status === 'synced');
      const failed = syncs.filter((s) => s.status === 'sync_failed');
      if (synced.length) toast.success(`Shopify: ${synced.length} order(s) marked paid (${synced.map((s) => s.order_number).join(', ')})`);
      if (failed.length) toast.error(`Shopify sync failed for ${failed.map((s) => s.order_number || s.order_id).join(', ')} — retry from order details`);
      setSettleFor(null); setSummary(null); setSettleNotes('');
      load();
      onSettled?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Settlement failed'); }
  };

  const retrySync = async (id) => {
    try {
      const resp = await axios.post(`${API}/manager-cash/entries/${id}/shopify-sync`, {}, auth());
      const st = resp.data.result?.status;
      toast[st === 'synced' ? 'success' : 'error'](`Shopify sync: ${st}${resp.data.result?.error ? ` — ${resp.data.result.error}` : ''}`);
      load();
    } catch (e) { toast.error('Sync failed'); }
  };

  const openLedger = async (managerId) => {
    try {
      const resp = await axios.get(`${API}/manager-cash/ledger?manager_id=${managerId}`, auth());
      setLedger(resp.data.ledger || []);
      setViewLedgerFor(wallets.find((w) => w.manager_id === managerId));
    } catch (e) { toast.error('Failed to load ledger'); }
  };

  const saveSettings = async (patch) => {
    try {
      const resp = await axios.put(`${API}/manager-cash/settings`, { ...settings, ...patch }, auth());
      setSettings(resp.data);
      toast.success('Settings saved');
    } catch (e) { toast.error('Failed to save settings'); }
  };

  const totalPending = (pending.ledger_items?.length || 0) + (pending.pending_expenses?.length || 0);

  return (
    <div className="bg-wt-paper border border-wt-border rounded-xl p-6" data-testid="admin-cash-panel">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-lg bg-purple-500/10"><Users className="h-6 w-6 text-purple-400" /></div>
          <div>
            <h2 className="text-xl font-semibold text-wt-text-primary">Manager Cash Ledgers</h2>
            <p className="text-wt-text-muted text-sm">Wallets, approvals, settlements & Shopify paid sync</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" className="border-wt-border" onClick={() => navigate('/cash/reports')} data-testid="cash-reports-btn">
            <BarChart3 className="w-4 h-4 mr-1" />Reports
          </Button>
          <Button variant="outline" size="sm" className="border-wt-border" onClick={() => setShowSettings(true)} data-testid="cash-settings-btn">
            <Settings2 className="w-4 h-4 mr-1" />Settings
          </Button>
        </div>
      </div>

      {/* Wallets */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3 mb-5" data-testid="manager-wallets-grid">
        {wallets.map((w) => (
          <div key={w.manager_id} className="rounded-lg border border-wt-border/50 p-4" data-testid={`wallet-card-${w.manager_id}`}>
            <div className="flex items-center justify-between mb-1">
              <span className="font-medium text-wt-text-primary text-sm">{w.manager_name}</span>
              <span className="text-lg font-bold text-wt-gold">{formatCurrency(w.current_balance)}</span>
            </div>
            <div className="text-[11px] text-wt-text-muted space-y-0.5">
              <div>Drivers: {formatCurrency(w.cash_from_drivers_held)} · Receipts: {formatCurrency(w.direct_receipts_approved)} · Expenses: −{formatCurrency(w.expenses_approved)}</div>
              {(w.direct_receipts_pending > 0 || w.expenses_pending > 0) && (
                <div className="text-amber-400">Pending: +{formatCurrency(w.direct_receipts_pending)} / −{formatCurrency(w.expenses_pending)}</div>
              )}
            </div>
            <div className="flex gap-2 mt-2">
              <Button variant="outline" size="sm" className="h-7 text-xs border-wt-border" onClick={() => openLedger(w.manager_id)} data-testid={`view-ledger-${w.manager_id}`}>Ledger</Button>
              <Button size="sm" className="h-7 text-xs" onClick={() => openSettle(w.manager_id)} data-testid={`settle-btn-${w.manager_id}`}>
                <Scale className="w-3 h-3 mr-1" />Settle
              </Button>
            </div>
          </div>
        ))}
        {wallets.length === 0 && <p className="text-sm text-wt-text-muted col-span-3">No manager cash activity yet</p>}
      </div>

      {/* Pending approvals */}
      <h3 className="text-sm font-medium text-wt-text-primary mb-2">Pending Approvals {totalPending > 0 && <Badge className="ml-1 bg-amber-500/20 text-amber-400 border-amber-500/40">{totalPending}</Badge>}</h3>
      <div className="space-y-2 mb-4" data-testid="pending-approvals-list">
        {(pending.ledger_items || []).map((it) => (
          <div key={it.id} className="flex items-center justify-between rounded border border-wt-border/50 p-3" data-testid={`pending-entry-${it.id}`}>
            <div className="text-xs text-wt-text-primary">
              <span className="font-medium">{it.manager_name}</span> · {it.type === 'adjustment' ? `Reversal request (${it.notes})` : it.type === 'pos_shift_cash' ? 'POS shift cash handover' : `Cash received — ${it.category}`}
              {it.order_number && <span className="text-wt-text-muted"> · Order {it.order_number}</span>}
              {it.customer_name && <span className="text-wt-text-muted"> · {it.customer_name}</span>}
              <div className="text-wt-text-muted">{(it.created_at || '').slice(0, 16).replace('T', ' ')} · {it.notes}</div>
              {(it.attachments || []).map((a) => (
                <a key={a.id} href={a.url} target="_blank" rel="noreferrer" className="text-blue-400 inline-flex items-center gap-1 mr-2">
                  <ExternalLink className="w-3 h-3" />{a.name}
                </a>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <span className={`font-semibold ${it.direction === 'in' ? 'text-emerald-400' : 'text-red-400'}`}>{formatCurrency(it.amount)}</span>
              <Button size="sm" className="h-7 text-xs" onClick={() => approve(it.id)} data-testid={`approve-entry-${it.id}`}><CheckCircle className="w-3 h-3 mr-1" />Approve</Button>
              <Button size="sm" variant="outline" className="h-7 text-xs border-red-500/40 text-red-400" onClick={() => setRejectFor(it)} data-testid={`reject-entry-${it.id}`}><XCircle className="w-3 h-3 mr-1" />Reject</Button>
            </div>
          </div>
        ))}
        {(pending.pending_expenses || []).map((ex) => (
          <div key={ex.id} className="flex items-center justify-between rounded border border-wt-border/50 p-3" data-testid={`pending-expense-${ex.id}`}>
            <div className="text-xs text-wt-text-primary">
              <span className="font-medium">{ex.manager_name}</span> · Expense from cash — {ex.title} ({ex.category_name})
              <div className="text-wt-text-muted">{(ex.created_at || '').slice(0, 16).replace('T', ' ')} · {ex.description}</div>
            </div>
            <div className="flex items-center gap-2">
              <span className="font-semibold text-red-400">−{formatCurrency(ex.amount)}</span>
              <Button size="sm" className="h-7 text-xs" onClick={() => approveExpense(ex.id)} data-testid={`approve-expense-${ex.id}`}>Approve</Button>
              <Button size="sm" variant="outline" className="h-7 text-xs border-red-500/40 text-red-400" onClick={() => rejectExpense(ex.id)} data-testid={`reject-expense-${ex.id}`}>Reject</Button>
            </div>
          </div>
        ))}
        {totalPending === 0 && <p className="text-sm text-wt-text-muted">Nothing pending approval</p>}
      </div>

      {/* Ledger dialog */}
      <Dialog open={!!viewLedgerFor} onOpenChange={(o) => !o && setViewLedgerFor(null)}>
        <DialogContent className="bg-wt-paper border-wt-border max-w-4xl">
          <DialogHeader><DialogTitle className="text-wt-text-primary">Ledger — {viewLedgerFor?.manager_name}</DialogTitle></DialogHeader>
          <div className="max-h-[60vh] overflow-auto">
            <LedgerTable ledger={ledger} formatCurrency={formatCurrency} />
          </div>
          <div className="text-xs text-wt-text-muted">
            Shopify sync: {ledger.filter((e) => e.shopify_sync).map((e) => (
              <span key={e.id} className={`mr-3 ${SYNC_STYLES[e.shopify_sync.status] || ''}`}>
                {e.order_number}: {e.shopify_sync.status}
                {e.shopify_sync.status === 'sync_failed' && (
                  <Button variant="ghost" size="sm" className="h-5 px-1 text-[10px]" onClick={() => retrySync(e.id)} data-testid={`retry-sync-${e.id}`}>
                    <RefreshCw className="w-3 h-3" />retry
                  </Button>
                )}
              </span>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Settlement dialog */}
      <Dialog open={!!settleFor} onOpenChange={(o) => !o && setSettleFor(null)}>
        <DialogContent className="bg-wt-paper border-wt-border max-w-lg">
          <DialogHeader><DialogTitle className="text-wt-text-primary">Settlement — {summary?.manager_name}</DialogTitle></DialogHeader>
          {summary && (
            <div className="text-sm space-y-1 text-wt-text-primary" data-testid="settlement-summary">
              <div className="flex justify-between"><span className="text-wt-text-muted">Cash from drivers (pending handover)</span><span>{formatCurrency(summary.orders_cash_sum)}</span></div>
              <div className="flex justify-between"><span className="text-wt-text-muted">Direct receipts (approved, unsettled)</span><span className="text-emerald-400">+{formatCurrency(summary.direct_receipts_unsettled)}</span></div>
              {summary.pos_cash_unsettled > 0 && (
                <div className="flex justify-between" data-testid="settle-pos-cash-row"><span className="text-wt-text-muted">POS shift cash (unsettled)</span><span className="text-emerald-400">+{formatCurrency(summary.pos_cash_unsettled)}</span></div>
              )}
              <div className="flex justify-between"><span className="text-wt-text-muted">Approved expenses deducted</span><span className="text-red-400">−{formatCurrency(summary.expenses_unsettled)}</span></div>
              {(summary.pending_receipts > 0 || summary.pending_expenses > 0) && (
                <div className="flex justify-between text-amber-400"><span>Pending (not included)</span><span>+{formatCurrency(summary.pending_receipts)} / −{formatCurrency(summary.pending_expenses)}</span></div>
              )}
              <div className="flex justify-between border-t border-wt-border/50 pt-1 font-semibold"><span>Expected cash with manager</span><span className="text-wt-gold" data-testid="expected-cash">{formatCurrency(summary.expected_cash)}</span></div>
              <div className="pt-2">
                <Label className="text-xs text-wt-text-muted">Cash actually submitted</Label>
                <Input type="number" value={actualSubmitted} className="mt-1" data-testid="actual-submitted-input"
                  onChange={(e) => setActualSubmitted(e.target.value)} />
                {Math.abs((parseFloat(actualSubmitted) || 0) - summary.expected_cash) > 0.01 && (
                  <p className={`text-xs mt-1 ${(parseFloat(actualSubmitted) || 0) < summary.expected_cash ? 'text-red-400' : 'text-emerald-400'}`} data-testid="difference-label">
                    {(parseFloat(actualSubmitted) || 0) < summary.expected_cash ? 'Shortage' : 'Excess'}: {formatCurrency(Math.abs((parseFloat(actualSubmitted) || 0) - summary.expected_cash))}
                  </p>
                )}
              </div>
              <Textarea value={settleNotes} rows={2} placeholder="Notes / clarification…" className="mt-2"
                onChange={(e) => setSettleNotes(e.target.value)} data-testid="settle-notes-input" />
              {settings?.auto_mark_shopify_paid && summary.orders_pending_settlement?.length > 0 && (
                <p className="text-[11px] text-emerald-400" data-testid="settle-shopify-note">
                  Shopify COD orders in this settlement will be marked paid automatically.
                </p>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="border-wt-border" onClick={() => setSettleFor(null)}>Cancel</Button>
            <Button onClick={settle} data-testid="settle-confirm-btn">Approve Settlement</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject dialog */}
      <Dialog open={!!rejectFor} onOpenChange={(o) => !o && setRejectFor(null)}>
        <DialogContent className="bg-wt-paper border-wt-border max-w-md">
          <DialogHeader><DialogTitle className="text-wt-text-primary">Reject Transaction</DialogTitle></DialogHeader>
          <Textarea value={rejectReason} rows={2} placeholder="Reason…" onChange={(e) => setRejectReason(e.target.value)} data-testid="reject-reason-input" />
          <DialogFooter><Button variant="destructive" onClick={reject} data-testid="reject-confirm-btn">Reject</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Settings dialog */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="bg-wt-paper border-wt-border max-w-md">
          <DialogHeader><DialogTitle className="text-wt-text-primary">Manager Cash Settings</DialogTitle></DialogHeader>
          {settings && (
            <div className="space-y-4 text-sm text-wt-text-primary">
              <div className="flex items-center justify-between">
                <span>Auto mark Shopify order paid on approval</span>
                <Switch checked={settings.auto_mark_shopify_paid} onCheckedChange={(v) => saveSettings({ auto_mark_shopify_paid: v })} data-testid="setting-auto-shopify" />
              </div>
              <div className="flex items-center justify-between">
                <span>Allow manager direct cash receipts</span>
                <Switch checked={settings.allow_manager_direct_receipt} onCheckedChange={(v) => saveSettings({ allow_manager_direct_receipt: v })} data-testid="setting-allow-receipt" />
              </div>
              <div className="flex items-center justify-between">
                <span>Direct receipts require admin approval</span>
                <Switch checked={settings.direct_receipt_requires_approval} onCheckedChange={(v) => saveSettings({ direct_receipt_requires_approval: v })} data-testid="setting-require-approval" />
              </div>
              <div>
                <Label className="text-xs text-wt-text-muted">Receipt/proof required above amount (expenses only)</Label>
                <Input type="number" defaultValue={settings.receipt_required_above} className="mt-1" data-testid="setting-receipt-above"
                  onBlur={(e) => saveSettings({ receipt_required_above: parseFloat(e.target.value) || 0 })} />
              </div>
              <div>
                <Label className="text-xs text-wt-text-muted">Allowed receipt categories (comma separated)</Label>
                <Input defaultValue={(settings.allowed_receipt_categories || []).join(', ')} className="mt-1" data-testid="setting-categories"
                  onBlur={(e) => saveSettings({ allowed_receipt_categories: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) })} />
              </div>
              <p className="text-[11px] text-wt-text-muted">Shopify sync trigger: {settings.shopify_sync_trigger === 'admin_approval' ? 'after Admin approval (recommended)' : 'on Manager entry'} — change:</p>
              <div className="flex items-center justify-between">
                <span>Require Admin approval before Shopify sync</span>
                <Switch checked={settings.shopify_sync_trigger === 'admin_approval'}
                  onCheckedChange={(v) => saveSettings({ shopify_sync_trigger: v ? 'admin_approval' : 'manager_approval' })} data-testid="setting-sync-trigger" />
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
});

export default AdminCashPanel;
